import { ChangeEvent, DragEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  AddCircleOutline,
  CalendarMonth,
  Cancel,
  Check,
  CheckCircle,
  CloudUpload,
  ContentCopy,
  Download,
  DriveFileRenameOutline,
  FileDownload,
  Groups,
  InfoOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Link as LinkIcon,
  LocationOn,
  VisibilityOutlined,
  QrCode2,
  RemoveCircle,
  Save,
  Schedule,
  Send,
  Close,
  Description,
  DeleteOutline,
  Edit,
} from "@mui/icons-material";
import * as QRCode from "qrcode";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  adicionarAlunoTurma,
  cancelarInscricaoAlunoTurma,
  createLinkPresencaTurma,
  createNotaManualTurma,
  createPresencaManualTurma,
  deleteLinkPresencaTurma,
  findInscritosTurma,
  findLinkPresencaAtivoTurma,
  findTurma,
  marcarTodosPresencaTurma,
  processarResultadosTurma,
  removerTodosPresencaTurma,
  uploadNotasCsvTurma,
  uploadPresencasCsvTurma,
} from "API/apiAcoes";
import { countUsuarios, fetchUsuarios } from "API/fetchUsuarios";
import CpfField from "components/atoms/CpfField/CpfField";
import ConfirmDialog from "components/atoms/ConfirmDialog/ConfirmDialog";
import MultiActionButton from "components/atoms/MultiActionButton/MultiActionButton";
import SelectField from "components/atoms/SelectField/SelectField";
import { useAuth } from "resources/AuthProvider";
import { CsvImportErrorItem, CsvImportResult, InscritoTurma, PresencaTurmaLink, TipoPresencaTurma, TurmaAcao } from "types/acao";
import { BaseUsuario } from "types/Models";
import { exportXLSX } from "utils/exporters/exportXLSX";
import { formatarCpf, formatarDataDbParaBr, formatarDatetimeCompletoBr, formatarDatetimeToDataBr } from "../../../utils/Masks";
import { montarEnderecoTurma } from "../../../utils/turmaEndereco";
import brasaoMinasGerais from "../../../assets/brasao_minas_gerais.png";

import styles from "./Turmas.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_filter from "../../../styles/Filter.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_table from "../../../styles/Table.module.css";

const statusTurmaConfig: Record<string, { label: string; className: string }> = {
  em_construcao: { label: "Em construção", className: styles.statusEmConstrucao },
  inscricoes_abertas: { label: "Inscrições abertas", className: styles.statusInscricoesAbertas },
  aguardando_inicio: { label: "Aguardando início", className: styles.statusAguardandoInicio },
  em_andamento: { label: "Em andamento", className: styles.statusEmAndamento },
  encerrada: { label: "Encerrada", className: styles.statusEncerrada },
};

const criterioAprovacaoLabels: Record<string, string> = {
  PRESENCA: "Presença",
  ATIVIDADE_AVALIATIVA: "Atividade avaliativa",
  PRESENCA_E_ATIVIDADE: "Presença e atividade",
};

const getStatusTurma = (status?: string) => {
  return status && statusTurmaConfig[status]
    ? statusTurmaConfig[status]
    : { label: status || "-", className: styles.statusDefault };
};

const formatarHora = (hora?: string | null) => (hora ? hora.slice(0, 5) : "-");

const criarLinkPresencaPublico = (codigo: string) => {
  const isAmbienteLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const baseUrl = isAmbienteLocal ? window.location.origin : "https://siscap.mg.gov.br";

  return `${baseUrl}/presenca-turma?cod=${encodeURIComponent(codigo)}`;
};

const criarDataLocal = (data: string) => {
  const [ano, mes, dia] = data.split("-").map(Number);
  return new Date(ano, mes - 1, dia);
};

const formatarDataDbLocal = (data: Date) => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

const gerarDiasTurma = (dataInicio?: string, dataFim?: string) => {
  if (!dataInicio || !dataFim) return [];

  const inicio = criarDataLocal(dataInicio);
  const fim = criarDataLocal(dataFim);
  const dias: string[] = [];

  for (let data = new Date(inicio); data <= fim; data.setDate(data.getDate() + 1)) {
    dias.push(formatarDataDbLocal(data));
  }

  return dias;
};

type StatusPresencaManual = "presente" | "ausente" | "nao_registrado";

const tipoPresencaPorStatus: Record<Exclude<StatusPresencaManual, "nao_registrado">, TipoPresencaTurma> = {
  presente: "PRESENTE",
  ausente: "AUSENTE",
};

const getStatusPresencaManual = (tipo?: TipoPresencaTurma): StatusPresencaManual => {
  if (tipo === "AUSENTE") return "ausente";
  if (tipo === "PRESENTE" || !tipo) return "presente";

  return "nao_registrado";
};

const getIniciaisNome = (nome?: string) => {
  const partes = (nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "--";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();

  return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
};

const mascararCpfCompacto = (cpf?: string) => {
  const apenasNumeros = (cpf || "").replace(/\D/g, "");
  if (apenasNumeros.length < 2) return "***.***.***-**";

  return `***.***.***-${apenasNumeros.slice(-2)}`;
};

const formatarDataHora = (data?: string | null) => {
  if (!data) return "-";

  const formatted = formatarDatetimeCompletoBr(data);
  const date = new Date(data);

  if (!formatted) {
    return "-";
  }

  return `${formatarDatetimeToDataBr(data)} às ${date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
};

const apenasNumeros = (value: string) => value.replace(/\D/g, "");

const normalizeText = (value?: string | null) =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const carregarImagem = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

const modeloCsvPresencas = [
  "cpf;data_presenca;tipo",
  "11111111111;2026-05-05;PRESENTE",
  "22222222222;2026-05-05;AUSENTE",
].join("\n");

const modeloCsvNotas = [
  "cpf;nota",
  "11111111111;80,5",
  "22222222222;59",
].join("\n");

const checkboxLabel = { inputProps: { "aria-label": "Selecionar registro" } };
const limiteErrosCsv = 10;

const tamanhoMaximoCsvPresenca = 5 * 1024 * 1024;

const ControlePresenca = () => {
  const navigate = useNavigate();
  const { user, hasActionPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("turma");

  const [turma, setTurma] = useState<TurmaAcao | null>(null);
  const [inscritos, setInscritos] = useState<InscritoTurma[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingInscritos, setLoadingInscritos] = useState<boolean>(false);
  const [loadingXls, setLoadingXls] = useState<boolean>(false);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [openExportar, setOpenExportar] = useState<boolean>(false);
  const [openListaPresenca, setOpenListaPresenca] = useState<boolean>(false);
  const [openQrCode, setOpenQrCode] = useState<boolean>(false);
  const [openGerarLink, setOpenGerarLink] = useState<boolean>(false);
  const [openEncerrarLink, setOpenEncerrarLink] = useState<boolean>(false);
  const [openUploadCsv, setOpenUploadCsv] = useState<boolean>(false);
  const [openUploadNotasCsv, setOpenUploadNotasCsv] = useState<boolean>(false);
  const [openLancamentoNotasManual, setOpenLancamentoNotasManual] = useState<boolean>(false);
  const [openConfirmProcessarResultados, setOpenConfirmProcessarResultados] = useState<boolean>(false);
  const [openAdicionarParticipante, setOpenAdicionarParticipante] = useState<boolean>(false);
  const [openConfirmAdicionarParticipante, setOpenConfirmAdicionarParticipante] = useState<boolean>(false);
  const [openConfirmMarcarTodosPresentes, setOpenConfirmMarcarTodosPresentes] = useState<boolean>(false);
  const [openLancamentoManual, setOpenLancamentoManual] = useState<boolean>(false);
  const [openConfirmLimparMarcacoes, setOpenConfirmLimparMarcacoes] = useState<boolean>(false);
  const [openConfirmSalvarPresencas, setOpenConfirmSalvarPresencas] = useState<boolean>(false);
  const [openConfirmCancelarInscricao, setOpenConfirmCancelarInscricao] = useState<boolean>(false);
  const [dataListaPresenca, setDataListaPresenca] = useState<string>("");
  const [dataLancamentoManual, setDataLancamentoManual] = useState<string>("");
  const [linkPresenca, setLinkPresenca] = useState<PresencaTurmaLink | null>(null);
  const [presencaUrl, setPresencaUrl] = useState<string>("");
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [loadingLinkPresenca, setLoadingLinkPresenca] = useState<boolean>(false);
  const [loadingEncerrarLink, setLoadingEncerrarLink] = useState<boolean>(false);
  const [loadingUploadCsv, setLoadingUploadCsv] = useState<boolean>(false);
  const [loadingUploadNotasCsv, setLoadingUploadNotasCsv] = useState<boolean>(false);
  const [loadingLancamentoNotas, setLoadingLancamentoNotas] = useState<boolean>(false);
  const [loadingNotaManualUsuario, setLoadingNotaManualUsuario] = useState<number | null>(null);
  const [loadingProcessarResultados, setLoadingProcessarResultados] = useState<boolean>(false);
  const [loadingBuscarUsuarios, setLoadingBuscarUsuarios] = useState<boolean>(false);
  const [loadingAdicionarParticipante, setLoadingAdicionarParticipante] = useState<boolean>(false);
  const [loadingMarcarTodosPresentes, setLoadingMarcarTodosPresentes] = useState<boolean>(false);
  const [loadingLancamentoManual, setLoadingLancamentoManual] = useState<boolean>(false);
  const [loadingLimparMarcacoes, setLoadingLimparMarcacoes] = useState<boolean>(false);
  const loadingSalvarPresencas = false;
  const [loadingCancelarInscricao, setLoadingCancelarInscricao] = useState<boolean>(false);
  const [openVisualizarPresencas, setOpenVisualizarPresencas] = useState<boolean>(false);
  const [openConfirmCancelarInscricoesMassa, setOpenConfirmCancelarInscricoesMassa] = useState<boolean>(false);
  const [loadingPresencaManualUsuario, setLoadingPresencaManualUsuario] = useState<number | null>(null);
  const [inscritoSelecionadoCancelamento, setInscritoSelecionadoCancelamento] = useState<InscritoTurma | null>(null);
  const [inscritoSelecionadoPresencas, setInscritoSelecionadoPresencas] = useState<InscritoTurma | null>(null);
  const [inscritosLancamentoManual, setInscritosLancamentoManual] = useState<InscritoTurma[]>([]);
  const [presencasLancamentoManual, setPresencasLancamentoManual] = useState<Record<number, StatusPresencaManual>>({});
  const [inscritosLancamentoNotas, setInscritosLancamentoNotas] = useState<InscritoTurma[]>([]);
  const [notasLancamentoManual, setNotasLancamentoManual] = useState<Record<number, string>>({});
  const [arquivoCsvPresenca, setArquivoCsvPresenca] = useState<File | null>(null);
  const [arquivoCsvNotas, setArquivoCsvNotas] = useState<File | null>(null);
  const [uploadCsvError, setUploadCsvError] = useState<string>("");
  const [uploadNotasCsvError, setUploadNotasCsvError] = useState<string>("");
  const [uploadCsvResult, setUploadCsvResult] = useState<CsvImportResult | null>(null);
  const [uploadNotasCsvResult, setUploadNotasCsvResult] = useState<CsvImportResult | null>(null);
  const [draggingCsv, setDraggingCsv] = useState<boolean>(false);
  const [draggingNotasCsv, setDraggingNotasCsv] = useState<boolean>(false);
  const [nomeFiltroUsuario, setNomeFiltroUsuario] = useState<string>("");
  const [cpfFiltroUsuario, setCpfFiltroUsuario] = useState<string>("");
  const [emailFiltroUsuario, setEmailFiltroUsuario] = useState<string>("");
  const [usuariosEncontrados, setUsuariosEncontrados] = useState<BaseUsuario[]>([]);
  const [usuarioSelecionadoMatricula, setUsuarioSelecionadoMatricula] = useState<BaseUsuario | null>(null);
  const [buscaUsuariosExecutada, setBuscaUsuariosExecutada] = useState<boolean>(false);
  const [usuariosBuscaPage, setUsuariosBuscaPage] = useState<number>(1);
  const [usuariosBuscaTotalPages, setUsuariosBuscaTotalPages] = useState<number>(1);
  const [usuariosBuscaError, setUsuariosBuscaError] = useState<string>("");
  const [linkFeedback, setLinkFeedback] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalInscritos, setTotalInscritos] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [nomeFiltroInscrito, setNomeFiltroInscrito] = useState<string>("");
  const [cpfFiltroInscrito, setCpfFiltroInscrito] = useState<string>("");
  const [emailFiltroInscrito, setEmailFiltroInscrito] = useState<string>("");
  const [municipioFiltroInscrito, setMunicipioFiltroInscrito] = useState<string>("");
  const [ambitoFiltroInscrito, setAmbitoFiltroInscrito] = useState<string>("");
  const [lotacaoOrgaoFiltroInscrito, setLotacaoOrgaoFiltroInscrito] = useState<string>("");
  const [situacaoFiltroInscrito, setSituacaoFiltroInscrito] = useState<string>("");
  const [selectedInscritos, setSelectedInscritos] = useState<Set<string>>(new Set());

  const diasTurma = useMemo(
    () => gerarDiasTurma(turma?.data_inicio, turma?.data_fim),
    [turma?.data_inicio, turma?.data_fim],
  );

  const statusTurma = getStatusTurma(turma?.status);
  const isPresencial = Number(turma?.modalidade_id) === 4;
  const localOuLink = isPresencial ? montarEnderecoTurma(turma) : turma?.link;
  const criterioAprovacao = turma?.criterio_aprovacao?.tipo
    ? criterioAprovacaoLabels[turma.criterio_aprovacao.tipo] ?? turma.criterio_aprovacao.tipo
    : "-";
  const porcentagemAprovacao = turma?.criterio_aprovacao?.porcentagem_aprovacao;
  const exibirGerenciamentoPresenca =
    turma?.criterio_aprovacao?.tipo === "PRESENCA" ||
    turma?.criterio_aprovacao?.tipo === "PRESENCA_E_ATIVIDADE";
  const exibirLancamentoNotas =
    turma?.criterio_aprovacao?.tipo === "ATIVIDADE_AVALIATIVA" ||
    turma?.criterio_aprovacao?.tipo === "PRESENCA_E_ATIVIDADE";
  const canListarInscritos =
    hasActionPermission("turmas.listar_inscritos_all") ||
    hasActionPermission("turmas.listar_inscritos_own");
  const canAdicionarInscrito =
    hasActionPermission("turmas.adicionar_inscrito_all") ||
    hasActionPermission("turmas.adicionar_inscrito_own");
  const canCadastrarAdicionarInscrito =
    hasActionPermission("turmas.cadastrar_adicionar_inscrito_all") ||
    hasActionPermission("turmas.cadastrar_adicionar_inscrito_own");
  const canGerarLink =
    hasActionPermission("turmas.gerar_link_all") ||
    hasActionPermission("turmas.gerar_link_own");
  const canLancarPresenca =
    hasActionPermission("turmas.lancar_presenca_all") ||
    hasActionPermission("turmas.lancar_presenca_own");
  const canLancarNotas =
    hasActionPermission("turmas.lancar_notas_all") ||
    hasActionPermission("turmas.lancar_notas_own");
  const canProcessarResultados =
    hasActionPermission("turmas.processar_resultados_all") ||
    hasActionPermission("turmas.processar_resultados_own");
  const canExcluirInscrito =
    hasActionPermission("turmas.excluir_inscrito_all") ||
    hasActionPermission("turmas.excluir_inscrito_own");
  const canEditarInscrito =
    hasActionPermission("turmas.editar_inscrito_all") ||
    hasActionPermission("turmas.editar_inscrito_own");
  const hasFiltrosInscritos = useMemo(
    () =>
      !!nomeFiltroInscrito.trim() ||
      !!cpfFiltroInscrito.trim() ||
      !!emailFiltroInscrito.trim() ||
      !!municipioFiltroInscrito.trim() ||
      !!ambitoFiltroInscrito.trim() ||
      !!lotacaoOrgaoFiltroInscrito.trim() ||
      !!situacaoFiltroInscrito.trim(),
    [
      ambitoFiltroInscrito,
      cpfFiltroInscrito,
      emailFiltroInscrito,
      lotacaoOrgaoFiltroInscrito,
      municipioFiltroInscrito,
      nomeFiltroInscrito,
      situacaoFiltroInscrito,
    ],
  );

  const allInscritosSelected = inscritos.length > 0 && inscritos.every((inscrito) => selectedInscritos.has(String(inscrito.id)));
  const someInscritosSelected = inscritos.some((inscrito) => selectedInscritos.has(String(inscrito.id)));

  const handleInscritoSelection = (inscritoId: string | number) => {
    const selectedId = String(inscritoId);

    setSelectedInscritos((prevSelected) => {
      const newSelected = new Set(prevSelected);

      if (newSelected.has(selectedId)) {
        newSelected.delete(selectedId);
      } else {
        newSelected.add(selectedId);
      }

      return newSelected;
    });
  };

  const handleSelectAllInscritos = () => {
    setSelectedInscritos((prevSelected) => {
      const newSelected = new Set(prevSelected);

      if (allInscritosSelected) {
        inscritos.forEach((inscrito) => newSelected.delete(String(inscrito.id)));
      } else {
        inscritos.forEach((inscrito) => newSelected.add(String(inscrito.id)));
      }

      return newSelected;
    });
  };

  const inscritoAtendeFiltros = (inscrito: InscritoTurma) => {
    const municipio = inscrito.inscricao?.municipio_nome ?? inscrito.dados_profissionais?.municipio_nome ?? "";
    const ambito = inscrito.inscricao?.ambito_nome ?? inscrito.dados_profissionais?.ambito_nome ?? "";
    const lotacaoOrgao = inscrito.dados_profissionais?.lotacao_nome ?? inscrito.dados_profissionais?.orgao_nome ?? "";
    const situacao = inscrito.resultado?.status ?? "";

    const nomeMatch = !nomeFiltroInscrito.trim() || normalizeText(inscrito.nome).includes(normalizeText(nomeFiltroInscrito));
    const cpfMatch = !cpfFiltroInscrito.trim() || apenasNumeros(inscrito.cpf).includes(apenasNumeros(cpfFiltroInscrito));
    const emailMatch = !emailFiltroInscrito.trim() || normalizeText(inscrito.email).includes(normalizeText(emailFiltroInscrito));
    const municipioMatch = !municipioFiltroInscrito.trim() || normalizeText(municipio).includes(normalizeText(municipioFiltroInscrito));
    const ambitoMatch = !ambitoFiltroInscrito.trim() || normalizeText(ambito).includes(normalizeText(ambitoFiltroInscrito));
    const lotacaoOrgaoMatch =
      !lotacaoOrgaoFiltroInscrito.trim() || normalizeText(lotacaoOrgao).includes(normalizeText(lotacaoOrgaoFiltroInscrito));
    const situacaoMatch = !situacaoFiltroInscrito.trim() || situacao === situacaoFiltroInscrito;

    return nomeMatch && cpfMatch && emailMatch && municipioMatch && ambitoMatch && lotacaoOrgaoMatch && situacaoMatch;
  };

  const carregarInscritosTabela = async (page = currentPage, itemsPerPage = perPage) => {
    if (!turmaId) return;

    try {
      setLoadingInscritos(true);

      if (hasFiltrosInscritos) {
        const result = await findInscritosTurma(turmaId, 1, 999999);
        const filtrados = (result.inscritos ?? []).filter(inscritoAtendeFiltros);
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        setInscritos(filtrados.slice(startIndex, endIndex));
        setTotalInscritos(filtrados.length);
        setTotalPages(Math.max(Math.ceil(filtrados.length / itemsPerPage), 1));
        return;
      }

      const result = await findInscritosTurma(turmaId, page, itemsPerPage);
      setInscritos(result.inscritos ?? []);
      setTotalInscritos(result.pagination?.total ?? 0);
      setTotalPages(result.pagination?.last_page ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao consultar inscritos da turma.");
    } finally {
      setLoadingInscritos(false);
    }
  };

  useEffect(() => {
    const carregarTurma = async () => {
      if (!turmaId) {
        setError("Turma não informada.");
        return;
      }

      try {
        setLoading(true);
        setError("");
        const result = await findTurma(turmaId);
        setTurma(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro ao consultar a turma.");
      } finally {
        setLoading(false);
      }
    };

    carregarTurma();
  }, [turmaId]);

  useEffect(() => {
    carregarInscritosTabela();
  }, [
    turmaId,
    currentPage,
    perPage,
    nomeFiltroInscrito,
    cpfFiltroInscrito,
    emailFiltroInscrito,
    municipioFiltroInscrito,
    ambitoFiltroInscrito,
    lotacaoOrgaoFiltroInscrito,
    situacaoFiltroInscrito,
  ]);

  useEffect(() => {
    if (diasTurma.length > 0 && !dataListaPresenca) {
      setDataListaPresenca(diasTurma[0]);
    }
  }, [diasTurma, dataListaPresenca]);

  useEffect(() => {
    if (diasTurma.length > 0 && !dataLancamentoManual) {
      setDataLancamentoManual(diasTurma[0]);
    }
  }, [diasTurma, dataLancamentoManual]);

  useEffect(() => {
    if (!openLancamentoManual || !dataLancamentoManual) return;

    const presencasDoDia = inscritosLancamentoManual.reduce<Record<number, StatusPresencaManual>>(
      (acc, inscrito) => {
        const presencaDoDia = inscrito.presencas?.find(
          (presenca) => presenca.data_presenca === dataLancamentoManual,
        );
        acc[inscrito.id] = presencaDoDia
          ? getStatusPresencaManual(presencaDoDia.tipo)
          : "nao_registrado";
        return acc;
      },
      {},
    );

    setPresencasLancamentoManual(presencasDoDia);
  }, [openLancamentoManual, dataLancamentoManual, inscritosLancamentoManual]);

  useEffect(() => {
    if (!openLancamentoNotasManual) return;

    const notas = inscritosLancamentoNotas.reduce<Record<number, string>>((acc, inscrito) => {
      acc[inscrito.id] = inscrito.nota?.nota !== null && inscrito.nota?.nota !== undefined
        ? String(inscrito.nota.nota)
        : "";
      return acc;
    }, {});

    setNotasLancamentoManual(notas);
  }, [openLancamentoNotasManual, inscritosLancamentoNotas]);

  const handleExportarInscritos = async () => {
    if (!turmaId) return;

    try {
      setLoadingXls(true);
      const result = await findInscritosTurma(turmaId, 1, 999999);
      const diasTurmaExportacao = gerarDiasTurma(turma?.data_inicio, turma?.data_fim);
      const colunasBase = [
        { key: "nome", label: "Nome" },
        { key: "cpf", label: "CPF" },
        { key: "email", label: "E-mail" },
        { key: "possui_deficiencia_label", label: "Possui deficiência" },
        { key: "estrutura_participacao", label: "Estrutura participação" },
        { key: "deficiencias_label", label: "Deficiências" },
        { key: "dados_profissionais.municipio_nome", label: "Município Atuação" },
        { key: "dados_profissionais.lotacao_nome", label: "Lotação" },
        { key: "dados_profissionais.vinculo_nome", label: "Vínculo" },
        { key: "dados_profissionais.funcao_nome", label: "Função" },
        { key: "dados_profissionais.superintendencia_nome", label: "Superintendência" },
        { key: "dados_profissionais.unidade_nome", label: "Unidade" },
        { key: "dados_profissionais.diretoria_regional_nome", label: "Diretoria Regional" },
        { key: "dados_profissionais.diretoria_interna_nome", label: "Diretoria Interna" },
        { key: "dados_profissionais.orgao_nome", label: "Orgão de Atuação" },
        { key: "dados_profissionais.instituicao_nome", label: "Instituição" },
        { key: "dados_profissionais.ambito_nome", label: "Âmbito de Atuação" },
        { key: "data_hora_inscricao", label: "Data da matrícula" },
        { key: "resultado_status", label: "Status do inscrito" },
      ];
      const colunasPresenca = diasTurmaExportacao.map((dia) => ({
        key: `presenca_${dia}`,
        label: `Presenca ${formatarDataDbParaBr(dia)}`,
      }));

      const inscritosExportacao = (result.inscritos ?? []).map((inscrito) => {
        const presencasPorDia = diasTurmaExportacao.reduce<Record<string, string>>((acc, dia) => {
          const presencasDoDia = (inscrito.presencas ?? [])
            .filter((presenca) => presenca.data_presenca === dia)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          const presencaDoDia = presencasDoDia[0];

          acc[`presenca_${dia}`] = presencaDoDia?.tipo === "PRESENTE" && presencaDoDia.created_at
            ? formatarDatetimeCompletoBr(presencaDoDia.created_at) ?? ""
            : "";

          return acc;
        }, {});

        return {
          ...inscrito,
          resultado_status: inscrito.resultado?.status ?? "",
          possui_deficiencia_label:
            inscrito.dados_pessoais?.possui_deficiencia === 1 || inscrito.dados_pessoais?.possui_deficiencia === true
              ? "Sim"
              : "Não",
          estrutura_participacao:
            inscrito.dados_pessoais?.possui_deficiencia === 1 || inscrito.dados_pessoais?.possui_deficiencia === true
              ? (inscrito.dados_pessoais?.estrutura_participacao ?? "")
              : "",
          deficiencias_label:
            inscrito.dados_pessoais?.possui_deficiencia === 1 || inscrito.dados_pessoais?.possui_deficiencia === true
              ? (inscrito.deficiencias ?? [])
                .map((item) => item.deficiencia)
                .filter(Boolean)
                .join(", ")
              : "",
          dados_profissionais: {
            ...inscrito.dados_profissionais,
            municipio_nome: inscrito.dados_profissionais?.municipio_nome ?? inscrito.inscricao?.municipio_nome ?? "",
            ambito_nome: inscrito.dados_profissionais?.ambito_nome ?? inscrito.inscricao?.ambito_nome ?? "",
            lotacao_nome: inscrito.dados_profissionais?.lotacao_nome ?? "",
            vinculo_nome: inscrito.dados_profissionais?.vinculo_nome ?? "",
            funcao_nome: inscrito.dados_profissionais?.funcao_nome ?? "",
            superintendencia_nome: inscrito.dados_profissionais?.superintendencia_nome ?? "",
            unidade_nome: inscrito.dados_profissionais?.unidade_nome ?? "",
            diretoria_regional_nome: inscrito.dados_profissionais?.diretoria_regional_nome ?? "",
            diretoria_interna_nome: inscrito.dados_profissionais?.diretoria_interna_nome ?? "",
            orgao_nome: inscrito.dados_profissionais?.orgao_nome ?? "",
            instituicao_nome: inscrito.dados_profissionais?.instituicao_nome ?? "",
          },
          data_hora_inscricao: inscrito.inscricao?.created_at
            ? formatarDatetimeCompletoBr(inscrito.inscricao.created_at) ?? ""
            : "",
          ...presencasPorDia,
        };
      });

      exportXLSX(
        [...colunasBase, ...colunasPresenca],
        inscritosExportacao,
        `inscritos_turma_${turmaId}.xlsx`,
      );
      setOpenExportar(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "não foi possível gerar o XLS.");
    } finally {
      setLoadingXls(false);
    }
  };

  const handleChangePerPage = (event: ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const getAmbitoFiltroLabel = (value: string) => {
    if (value === "Estadual") return "Estadual";
    if (value === "Municipal") return "Municipal";
    return "";
  };

  const getSituacaoFiltroLabel = (value: string) => {
    if (value === "APROVADO") return "Aprovado";
    if (value === "REPROVADO") return "Reprovado";
    return "";
  };

  const temFiltroBuscaUsuario = () => (
    !!nomeFiltroUsuario.trim() ||
    !!cpfFiltroUsuario.trim() ||
    !!emailFiltroUsuario.trim()
  );

  const handleOpenAdicionarParticipante = () => {
    setOpenAdicionarParticipante(true);
    setUsuariosEncontrados([]);
    setBuscaUsuariosExecutada(false);
    setUsuariosBuscaError("");
    setUsuariosBuscaPage(1);
    setUsuariosBuscaTotalPages(1);
  };

  const handleBuscarUsuarios = async (page = 1) => {
    if (!user) return;

    if (!temFiltroBuscaUsuario()) {
      setUsuariosBuscaError("Preencha nome, CPF ou e-mail para pesquisar.");
      setUsuariosEncontrados([]);
      setBuscaUsuariosExecutada(false);
      return;
    }

    try {
      setLoadingBuscarUsuarios(true);
      setUsuariosBuscaError("");
      const cpfFiltro = apenasNumeros(cpfFiltroUsuario);
      const [usuarios, totalUsuarios] = await Promise.all([
        fetchUsuarios(
          page,
          10,
          user,
          nomeFiltroUsuario.trim(),
          "",
          undefined,
          undefined,
          undefined,
          undefined,
          cpfFiltro,
          emailFiltroUsuario.trim(),
        ),
        countUsuarios(
          user,
          nomeFiltroUsuario.trim(),
          "",
          undefined,
          undefined,
          undefined,
          undefined,
          cpfFiltro,
          emailFiltroUsuario.trim(),
        ),
      ]);

      setUsuariosEncontrados(usuarios);
      setUsuariosBuscaPage(page);
      setUsuariosBuscaTotalPages(Math.max(Math.ceil(totalUsuarios / 10), 1));
      setBuscaUsuariosExecutada(true);
    } catch (err) {
      setUsuariosBuscaError(err instanceof Error ? err.message : "Erro ao buscar usuários.");
    } finally {
      setLoadingBuscarUsuarios(false);
    }
  };

  const handleLimparBuscaUsuarios = () => {
    setNomeFiltroUsuario("");
    setCpfFiltroUsuario("");
    setEmailFiltroUsuario("");
    setUsuariosEncontrados([]);
    setUsuariosBuscaError("");
    setBuscaUsuariosExecutada(false);
    setUsuariosBuscaPage(1);
    setUsuariosBuscaTotalPages(1);
  };

  const handleOpenConfirmAdicionarParticipante = (usuario: BaseUsuario) => {
    setUsuarioSelecionadoMatricula(usuario);
    setOpenConfirmAdicionarParticipante(true);
  };

  const handleAdicionarParticipante = async () => {
    if (!turmaId || !usuarioSelecionadoMatricula) return;

    try {
      setLoadingAdicionarParticipante(true);
      setError("");
      setLinkFeedback("");
      const response = await adicionarAlunoTurma(turmaId, Number(usuarioSelecionadoMatricula.id));
      await carregarInscritosTabela();
      setOpenConfirmAdicionarParticipante(false);
      setOpenAdicionarParticipante(false);
      setUsuarioSelecionadoMatricula(null);
      setLinkFeedback(response?.message || "Participante adicionado a turma com sucesso.");
    } catch (err) {
      setUsuariosBuscaError(err instanceof Error ? err.message : "Erro ao adicionar participante na turma.");
    } finally {
      setLoadingAdicionarParticipante(false);
    }
  };

  const handleCadastrarNovoParticipante = () => {
    if (!turmaId) return;
    navigate(`/controle-presenca/cadastrar-participante?turma=${turmaId}`);
  };

  const handleEditarDadosInscrito = (inscrito: InscritoTurma) => {
    navigate(`/user?cpf=${inscrito.cpf}`);
  };

  const handleOpenVisualizarPresencas = (inscrito: InscritoTurma) => {
    setInscritoSelecionadoPresencas(inscrito);
    setOpenVisualizarPresencas(true);
  };

  const handleOpenCancelarInscricao = (inscrito: InscritoTurma) => {
    setInscritoSelecionadoCancelamento(inscrito);
    setOpenConfirmCancelarInscricao(true);
  };

  const handleCancelarInscricao = async () => {
    if (!turmaId || !inscritoSelecionadoCancelamento) return;

    try {
      setLoadingCancelarInscricao(true);
      setError("");
      setLinkFeedback("");
      const response = await cancelarInscricaoAlunoTurma(turmaId, inscritoSelecionadoCancelamento.id);
      await carregarInscritosTabela();
      setOpenConfirmCancelarInscricao(false);
      setInscritoSelecionadoCancelamento(null);
      setLinkFeedback(response.message || "inscrição cancelada com sucesso.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao cancelar inscrição do aluno.");
    } finally {
      setLoadingCancelarInscricao(false);
    }
  };

  const handleCancelarInscricoesMassa = async () => {
    if (!turmaId || selectedInscritos.size === 0) return;

    try {
      setLoadingCancelarInscricao(true);
      setError("");
      setLinkFeedback("");

      const inscritosSelecionados = inscritos.filter((inscrito) => selectedInscritos.has(String(inscrito.id)));

      for (const inscrito of inscritosSelecionados) {
        await cancelarInscricaoAlunoTurma(turmaId, inscrito.id);
      }

      await carregarInscritosTabela();
      setOpenConfirmCancelarInscricoesMassa(false);
      setSelectedInscritos(new Set());
      setLinkFeedback("Inscrições canceladas com sucesso.");
    } catch (err) {
      setOpenConfirmCancelarInscricoesMassa(false);
      setError(err instanceof Error ? err.message : "Erro ao cancelar inscrições selecionadas.");
    } finally {
      setLoadingCancelarInscricao(false);
    }
  };

  const gerarPdfListaPresenca = async (inscritosLista: InscritoTurma[]) => {
    if (!turma) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const marginLeft = 36;
    const dataFormatada = formatarDataDbParaBr(dataListaPresenca);
    const enderecoTurma = montarEnderecoTurma(turma) || localOuLink || "-";
    const horaInicio = formatarHora(turma.hora_inicio);
    const horaFim = formatarHora(turma.hora_fim);
    const turno = horaInicio === "-" && horaFim === "-" ? "-" : `${horaInicio} às ${horaFim}`;

    const logo = await carregarImagem(brasaoMinasGerais);
    doc.addImage(logo, "PNG", marginLeft, 24, 58, 58);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("GOVERNO DO ESTADO DE MINAS GERAIS", 106, 38);
    doc.setFont("helvetica", "normal");
    doc.text("Secretaria de Estado de Desenvolvimento Social-SEDESE", 106, 54);
    doc.text("Subsecretaria de Assistência Social -SUBAS", 106, 70);

    autoTable(doc, {
      startY: 96,
      margin: { left: marginLeft, right: marginLeft },
      head: [[{ content: "LISTA DE PRESENÇA", colSpan: 2, styles: { halign: "center" } }]],
      body: [
        ["Curso", turma.acao || "-"],
        ["Endereço", enderecoTurma],
        ["Turma", turma.nome || "-"],
        ["Data", dataFormatada || "-"],
        ["Turno", turno],
      ],
      theme: "grid",
      styles: {
        fontSize: 10,
        textColor: [0, 0, 0],
        lineColor: [60, 60, 60],
        lineWidth: 0.8,
        cellPadding: 4,
        minCellHeight: 18,
      },
      headStyles: {
        fillColor: [190, 190, 190],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 92, fontStyle: "bold" },
        1: { cellWidth: 678 },
      },
    });

    const participantesStartY = ((doc as any).lastAutoTable?.finalY ?? 206) + 16;

    autoTable(doc, {
      startY: participantesStartY,
      margin: { left: marginLeft, right: marginLeft },
      head: [["Nº", "Nome", "CPF", "Município", "Âmbito", "E-mail", "Assinatura"]],
      body: inscritosLista.map((inscrito, index) => [
        String(index + 1),
        inscrito.nome || "-",
        formatarCpf(inscrito.cpf),
        inscrito.inscricao?.municipio_nome || "-",
        inscrito.inscricao?.ambito_nome || "-",
        inscrito.email || "-",
        "",
      ]),
      theme: "grid",
      rowPageBreak: "avoid",
      styles: {
        fontSize: 9,
        textColor: [0, 0, 0],
        lineColor: [60, 60, 60],
        lineWidth: 0.8,
        cellPadding: 4,
        minCellHeight: 38,
        valign: "middle",
      },
      headStyles: {
        fillColor: [190, 190, 190],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 28, halign: "center" },
        1: { cellWidth: 150 },
        2: { cellWidth: 75 },
        3: { cellWidth: 95 },
        4: { cellWidth: 65 },
        5: { cellWidth: 157 },
        6: { cellWidth: 200 },
      },
    });

    doc.save(`lista_presenca_turma_${turma.id}_${dataListaPresenca}.pdf`);
  };

  const handleGerarListaPresenca = async () => {
    if (!turmaId || !dataListaPresenca) return;

    try {
      setLoadingPdf(true);
      const result = await findInscritosTurma(turmaId, 1, 999999);
      await gerarPdfListaPresenca(result.inscritos ?? []);
      setOpenListaPresenca(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "não foi possível gerar a lista de Presença.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const carregarInscritosLancamentoManual = async () => {
    if (!turmaId) return;

    try {
      setLoadingLancamentoManual(true);
      setError("");
      const result = await findInscritosTurma(turmaId, 1, 999999);
      setInscritosLancamentoManual(result.inscritos ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao consultar inscritos da turma.");
    } finally {
      setLoadingLancamentoManual(false);
    }
  };

  const handleOpenLancamentoManual = () => {
    setOpenLancamentoManual(true);
    setDataLancamentoManual((dataAtual) => dataAtual || diasTurma[0] || "");
    carregarInscritosLancamentoManual();
  };

  const carregarInscritosLancamentoNotas = async () => {
    if (!turmaId) return;

    try {
      setLoadingLancamentoNotas(true);
      setError("");
      const result = await findInscritosTurma(turmaId, 1, 999999);
      setInscritosLancamentoNotas(result.inscritos ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao consultar inscritos da turma.");
    } finally {
      setLoadingLancamentoNotas(false);
    }
  };

  const handleOpenLancamentoNotasManual = () => {
    setOpenLancamentoNotasManual(true);
    carregarInscritosLancamentoNotas();
  };

  const handleOpenUploadCsv = () => {
    setArquivoCsvPresenca(null);
    setUploadCsvError("");
    setUploadCsvResult(null);
    setDraggingCsv(false);
    setOpenUploadCsv(true);
  };

  const handleOpenUploadNotasCsv = () => {
    setArquivoCsvNotas(null);
    setUploadNotasCsvError("");
    setUploadNotasCsvResult(null);
    setDraggingNotasCsv(false);
    setOpenUploadNotasCsv(true);
  };

  const validarArquivoCsvPresenca = (arquivo: File) => {
    if (!arquivo.name.toLowerCase().endsWith(".csv")) {
      return "Somente arquivos do tipo .csv são permitidos.";
    }

    if (arquivo.size > tamanhoMaximoCsvPresenca) {
      return "Tamanho máximo permitido: 5 MB.";
    }

    return "";
  };

  const selecionarArquivoCsvPresenca = (arquivo?: File) => {
    if (!arquivo) return;

    const erroArquivo = validarArquivoCsvPresenca(arquivo);
    if (erroArquivo) {
      setArquivoCsvPresenca(null);
      setUploadCsvError(erroArquivo);
      return;
    }

    setArquivoCsvPresenca(arquivo);
    setUploadCsvError("");
    setUploadCsvResult(null);
  };

  const selecionarArquivoCsvNotas = (arquivo?: File) => {
    if (!arquivo) return;

    const erroArquivo = validarArquivoCsvPresenca(arquivo);
    if (erroArquivo) {
      setArquivoCsvNotas(null);
      setUploadNotasCsvError(erroArquivo);
      return;
    }

    setArquivoCsvNotas(arquivo);
    setUploadNotasCsvError("");
    setUploadNotasCsvResult(null);
  };

  const handleChangeArquivoCsvPresenca = (event: ChangeEvent<HTMLInputElement>) => {
    selecionarArquivoCsvPresenca(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleChangeArquivoCsvNotas = (event: ChangeEvent<HTMLInputElement>) => {
    selecionarArquivoCsvNotas(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDropArquivoCsvPresenca = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDraggingCsv(false);
    selecionarArquivoCsvPresenca(event.dataTransfer.files?.[0]);
  };

  const handleDropArquivoCsvNotas = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDraggingNotasCsv(false);
    selecionarArquivoCsvNotas(event.dataTransfer.files?.[0]);
  };

  const baixarModeloCsvPresenca = () => {
    const blob = new Blob([modeloCsvPresencas], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo_presencas_turma.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const baixarModeloCsvNotas = () => {
    const blob = new Blob([modeloCsvNotas], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "modelo_notas_turma.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const baixarTxtErrosImportacao = (nomeArquivo: string, erros: CsvImportErrorItem[]) => {
    const conteudo = erros
      .map((erro) =>
        [
          `Linha: ${erro.linha}`,
          erro.cpf ? `CPF: ${erro.cpf}` : null,
          erro.data_presenca ? `Data: ${erro.data_presenca}` : null,
          `Erro: ${erro.erro}`,
        ]
          .filter(Boolean)
          .join(" | "),
      )
      .join("\n");

    const blob = new Blob([conteudo], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = nomeArquivo;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderImportResult = (resultado: CsvImportResult | null, tipoArquivo: "presencas" | "notas") => {
    if (!resultado) return null;

    const erros = resultado.data?.erros ?? [];
    const errosVisiveis = erros.slice(0, limiteErrosCsv);
    const possuiMaisErros = erros.length > limiteErrosCsv;

    return (
      <div className={styles.uploadCsvResultBox}>
        <Alert severity={resultado.success ? "success" : "warning"} className={styles.uploadCsvAlert}>
          {resultado.success
            ? "Importação processada com sucesso."
            : "A importação foi processada com inconsistências."}
        </Alert>

        <div className={styles.uploadCsvSummaryGrid}>
          <div className={styles.uploadCsvSummaryItem}>
            <strong>Processados</strong>
            <span>{resultado.data.processados}</span>
          </div>
          <div className={styles.uploadCsvSummaryItem}>
            <strong>Criados</strong>
            <span>{resultado.data.criados}</span>
          </div>
          <div className={styles.uploadCsvSummaryItem}>
            <strong>Atualizados</strong>
            <span>{resultado.data.atualizados}</span>
          </div>
          <div className={styles.uploadCsvSummaryItem}>
            <strong>Ignorados</strong>
            <span>{resultado.data.ignorados}</span>
          </div>
        </div>

        {!resultado.success && erros.length > 0 && (
          <div className={styles.uploadCsvErrorBox}>
            <strong className={styles.uploadCsvErrorTitle}>Primeiras linhas com erro</strong>
            <div className={styles.uploadCsvErrorList}>
              {errosVisiveis.map((erro, index) => (
                <div key={`${erro.linha}-${index}`} className={styles.uploadCsvErrorItem}>
                  <span><strong>Linha:</strong> {erro.linha}</span>
                  {Object.entries(erro)
                    .filter(([chave, valor]) => !["linha", "erro"].includes(chave) && valor !== null && valor !== undefined && String(valor).trim() !== "")
                    .map(([chave, valor]) => (
                      <span key={chave}>
                        <strong>{chave.replace(/_/g, " ")}:</strong> {String(valor)}
                      </span>
                    ))}
                  <span><strong>Erro:</strong> {erro.erro}</span>
                </div>
              ))}
            </div>

            {possuiMaisErros && (
              <button
                type="button"
                className={styles.uploadCsvDownload}
                onClick={() =>
                  baixarTxtErrosImportacao(
                    tipoArquivo === "presencas" ? "erros_importacao_presencas.txt" : "erros_importacao_notas.txt",
                    erros,
                  )
                }
              >
                <Description />
                Baixar arquivo TXT com todos os erros
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleEnviarUploadCsv = async () => {
    if (!turmaId) return;

    if (!arquivoCsvPresenca) {
      setUploadCsvError("Selecione um arquivo CSV para enviar.");
      return;
    }

    try {
      setLoadingUploadCsv(true);
      setUploadCsvError("");
      setError("");
      const response = await uploadPresencasCsvTurma(turmaId, arquivoCsvPresenca);
      setUploadCsvResult(response);
      await carregarInscritosTabela();
      setArquivoCsvPresenca(null);
      if (response.success) {
        setLinkFeedback("Importação de presenças concluída com sucesso.");
      } else {
        setUploadCsvError("A importação de presenças retornou inconsistências. Verifique o detalhamento abaixo.");
      }
    } catch (err) {
      setUploadCsvResult(null);
      setUploadCsvError(err instanceof Error ? err.message : "Erro ao enviar CSV de Presenças.");
    } finally {
      setLoadingUploadCsv(false);
    }
  };

  const handleEnviarUploadNotasCsv = async () => {
    if (!turmaId) return;

    if (!arquivoCsvNotas) {
      setUploadNotasCsvError("Selecione um arquivo CSV para enviar.");
      return;
    }

    try {
      setLoadingUploadNotasCsv(true);
      setUploadNotasCsvError("");
      setError("");
      const response = await uploadNotasCsvTurma(turmaId, arquivoCsvNotas);
      setUploadNotasCsvResult(response);
      await carregarInscritosTabela();
      setArquivoCsvNotas(null);
      if (response.success) {
        setLinkFeedback("Importação de notas concluída com sucesso.");
      } else {
        setUploadNotasCsvError("A importação de notas retornou inconsistências. Verifique o detalhamento abaixo.");
      }
    } catch (err) {
      setUploadNotasCsvResult(null);
      setUploadNotasCsvError(err instanceof Error ? err.message : "Erro ao enviar CSV de notas.");
    } finally {
      setLoadingUploadNotasCsv(false);
    }
  };

  const handleChangeNotaManual = (userId: number, nota: string) => {
    const notaNormalizada = nota.replace(",", ".");
    if (notaNormalizada && !/^\d{0,3}(\.\d{0,2})?$/.test(notaNormalizada)) return;

    setNotasLancamentoManual((notasAtuais) => ({
      ...notasAtuais,
      [userId]: notaNormalizada,
    }));
  };

  const handleSalvarNotaManual = async (inscrito: InscritoTurma) => {
    if (!turmaId) return;

    const notaTexto = notasLancamentoManual[inscrito.id];
    const nota = Number(notaTexto);

    if (notaTexto === "" || Number.isNaN(nota)) {
      setError("Informe uma nota válida.");
      return;
    }

    try {
      setLoadingNotaManualUsuario(inscrito.id);
      setError("");
      const response = await createNotaManualTurma(turmaId, inscrito.id, nota);
      setInscritosLancamentoNotas((inscritosAtuais) =>
        inscritosAtuais.map((item) => item.id === inscrito.id ? { ...item, nota: response.data } : item),
      );
      setInscritos((inscritosAtuais) =>
        inscritosAtuais.map((item) => item.id === inscrito.id ? { ...item, nota: response.data } : item),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao lançar nota manual.");
    } finally {
      setLoadingNotaManualUsuario(null);
    }
  };

  const handleProcessarResultados = async () => {
    if (!turmaId) return;

    try {
      setLoadingProcessarResultados(true);
      setOpenConfirmProcessarResultados(false);
      setError("");
      setLinkFeedback("");
      const response = await processarResultadosTurma(turmaId);
      await carregarInscritosTabela();
      setLinkFeedback(response?.message || "Resultados processados com sucesso.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao processar resultados da turma.");
    } finally {
      setLoadingProcessarResultados(false);
    }
  };

  const handleMarcarPresencaManual = async (
    inscrito: InscritoTurma,
    status: Exclude<StatusPresencaManual, "nao_registrado">,
  ) => {
    if (!turmaId || !dataLancamentoManual) return;

    try {
      setLoadingPresencaManualUsuario(inscrito.id);
      const response = await createPresencaManualTurma(
        turmaId,
        inscrito.id,
        dataLancamentoManual,
        tipoPresencaPorStatus[status],
      );

      setPresencasLancamentoManual((presencasAtuais) => ({
        ...presencasAtuais,
        [inscrito.id]: status,
      }));
      setInscritosLancamentoManual((inscritosAtuais) =>
        inscritosAtuais.map((item) => {
          if (item.id !== inscrito.id) return item;

          const presencasSemDia = (item.presencas ?? []).filter(
            (presenca) => presenca.data_presenca !== dataLancamentoManual,
          );

          return {
            ...item,
            presencas: [...presencasSemDia, response.data],
          };
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao lançar Presença manual.");
    } finally {
      setLoadingPresencaManualUsuario(null);
    }
  };

  const handleConfirmMarcarTodosPresentes = async () => {
    if (!turmaId || !dataLancamentoManual) return;

    try {
      setLoadingMarcarTodosPresentes(true);
      setError("");
      await marcarTodosPresencaTurma(turmaId, dataLancamentoManual);
      setOpenConfirmMarcarTodosPresentes(false);
      setPresencasLancamentoManual(
        inscritosLancamentoManual.reduce<Record<number, StatusPresencaManual>>((acc, inscrito) => {
          acc[inscrito.id] = "presente";
          return acc;
        }, {}),
      );
      await carregarInscritosLancamentoManual();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao marcar todos como presentes.");
    } finally {
      setLoadingMarcarTodosPresentes(false);
    }
  };

  const handleConfirmLimparMarcacoes = async () => {
    if (!turmaId || !dataLancamentoManual) return;

    try {
      setLoadingLimparMarcacoes(true);
      setError("");
      await removerTodosPresencaTurma(turmaId, dataLancamentoManual);
      setOpenConfirmLimparMarcacoes(false);
      setPresencasLancamentoManual(
        inscritosLancamentoManual.reduce<Record<number, StatusPresencaManual>>((acc, inscrito) => {
          acc[inscrito.id] = "nao_registrado";
          return acc;
        }, {}),
      );
      await carregarInscritosLancamentoManual();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao limpar marcações.");
    } finally {
      setLoadingLimparMarcacoes(false);
    }
  };

  const handleConfirmSalvarPresencas = () => {
    setOpenConfirmSalvarPresencas(false);
    setOpenLancamentoManual(false);
  };

  const consultarOuCriarLinkPresenca = async () => {
    if (!turmaId) {
      throw new Error("Turma não informada.");
    }

    const linkAtivo = await findLinkPresencaAtivoTurma(turmaId);
    if (linkAtivo.data) {
      return linkAtivo.data;
    }

    const linkCriado = await createLinkPresencaTurma(turmaId);
    if (!linkCriado.data) {
      throw new Error("não foi possível gerar o link de Presença.");
    }

    return linkCriado.data;
  };

  const prepararLinkPresenca = async () => {
    try {
      setLoadingLinkPresenca(true);
      setLinkFeedback("");
      const link = await consultarOuCriarLinkPresenca();
      const url = criarLinkPresencaPublico(link.codigo);
      const qrCode = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 320,
        color: {
          dark: "#222936",
          light: "#FFFFFF",
        },
      });

      setLinkPresenca(link);
      setPresencaUrl(url);
      setQrCodeUrl(qrCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao gerar link de Presença.");
    } finally {
      setLoadingLinkPresenca(false);
    }
  };

  const handleOpenQrCode = () => {
    setOpenQrCode(true);
    setQrCodeUrl("");
    setPresencaUrl("");
    prepararLinkPresenca();
  };

  const handleOpenGerarLink = () => {
    setOpenGerarLink(true);
    setQrCodeUrl("");
    setPresencaUrl("");
    prepararLinkPresenca();
  };

  const copiarLinkPresenca = async () => {
    if (!presencaUrl) return;

    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(presencaUrl);
      } else {
        const input = document.createElement("textarea");
        input.value = presencaUrl;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        input.style.pointerEvents = "none";
        document.body.appendChild(input);
        input.focus();
        input.select();

        const copied = document.execCommand("copy");

        document.body.removeChild(input);

        if (!copied) {
          throw new Error("copy_failed");
        }
      }
      setLinkFeedback("Link copiado.");
    } catch {
      setLinkFeedback("não foi possível copiar automaticamente. Selecione o link para copiar.");
    }
  };

  const baixarQrCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `qr-code-presenca-turma-${turmaId}.png`;
    link.click();
  };

  const handleEncerrarLinkPresenca = async () => {
    if (!turmaId) return;

    try {
      setLoadingEncerrarLink(true);
      setLinkFeedback("");
      const linkAtivo = await findLinkPresencaAtivoTurma(turmaId);

      if (!linkAtivo.data) {
        setOpenEncerrarLink(false);
        setLinkPresenca(null);
        setPresencaUrl("");
        setQrCodeUrl("");
        setError("não existe QR Code ou link ativo para encerrar.");
        return;
      }

      await deleteLinkPresencaTurma(turmaId, linkAtivo.data.codigo);
      setOpenEncerrarLink(false);
      setLinkPresenca(null);
      setPresencaUrl("");
      setQrCodeUrl("");
      setLinkFeedback("QR Code ou link encerrado com sucesso.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao encerrar QR Code ou link.");
    } finally {
      setLoadingEncerrarLink(false);
    }
  };

  const handleBack = () => {
    if (turma?.acao_id) {
      navigate(`/gestao-turmas?id=${turma.acao_id}`);
      return;
    }

    navigate(-1);
  };

  return (
    <Card className={styles.container}>
      {loadingProcessarResultados && (
        <div className={styles.processamentoResultadosOverlay}>
          <CircularProgress />
          <span>Processando resultados da turma...</span>
        </div>
      )}

      <CardContent>
        <div className={styles.title}>Inscritos da Turma</div>

        {loading && (
          <div className={styles_general.mt2}>
            <CircularProgress />
          </div>
        )}

        {error && (
          <div className={styles_general.mt2}>
            <Alert severity="error">{error}</Alert>
          </div>
        )}

        {!loading && turma && (
          <>
            <h2 className={styles.subtitlePresenca}>{turma.acao} - {turma.nome}</h2>

            <section className={styles.turmaInfoPanel}>
              <div className={styles.turmaInfoItem}>
                <span className={`${styles.infoLabel} ${styles_general.textAlignCenter}`}>Status da Turma</span>
                <span className={`${styles.statusBadge} ${statusTurma.className} ${styles_general.textAlignCenter}`}>{statusTurma.label}</span>
              </div>

              <div className={styles.turmaInfoDivider} />

              <div className={styles.turmaInfoItemIcon}>
                <CalendarMonth className={styles.infoIcon} />
                <div>
                  <span className={styles.infoLabel}>Período da Turma</span>
                  <span className={styles.infoText}>
                    {formatarDataDbParaBr(turma.data_inicio)} a {formatarDataDbParaBr(turma.data_fim)}
                  </span>
                </div>
              </div>

              <div className={styles.turmaInfoDivider} />

              <div className={styles.turmaInfoItemIcon}>
                <Schedule className={styles.infoIcon} />
                <div>
                  <span className={styles.infoLabel}>Horário da Turma</span>
                  <span className={styles.infoText}>
                    {formatarHora(turma.hora_inicio)} às {formatarHora(turma.hora_fim)}
                  </span>
                </div>
              </div>

              <div className={styles.turmaInfoDivider} />

              <div className={styles.turmaInfoItemIcon}>
                {isPresencial ? <LocationOn className={styles.infoIcon} /> : <LinkIcon className={styles.infoIcon} />}
                <div>
                  <span className={styles.infoLabel}>{isPresencial ? "Local da turma" : "Link da turma"}</span>
                  <span className={styles.infoText}>{localOuLink || "-"}</span>
                </div>
              </div>
            </section>

            {isPresencial && (
              <Alert severity="success" className={styles.criterioAlert}>
                <strong>Critério(s) de aprovação da turma:</strong>{" "}
                <span className={styles.criterioBadge}>{criterioAprovacao}</span>
                {porcentagemAprovacao !== null && porcentagemAprovacao !== undefined && (
                  <span className={styles.criterioBadge}>aprovação: {porcentagemAprovacao}%</span>
                )}
                <div>Registre a Presença dos participantes através de QRCode, link, Lançamento manual ou upload de lista CSV</div>
              </Alert>
            )}

            <section className={styles.presencaActionsPanel}>
              <div className={styles.participantesTotal}>
                <Groups className={styles.participantesIcon} />
                <strong>PARTICIPANTES INSCRITOS ({totalInscritos})</strong>
              </div>

              {canListarInscritos && <Button
                variant="contained"
                className={styles.presencaActionButton}
                startIcon={<Download />}
                onClick={() => setOpenExportar(true)}
              >
                Relatório inscritos
              </Button>}

              {canListarInscritos && <Button
                variant="contained"
                className={styles.presencaActionButton}
                startIcon={<Download />}
                onClick={() => setOpenListaPresenca(true)}
              >
                Gerar lista de Presença
              </Button>}

              {canAdicionarInscrito && <Button
                variant="contained"
                className={styles_button.buttonSquareSuccess}
                startIcon={<AddCircleOutline />}
                onClick={handleOpenAdicionarParticipante}
              >
                Adicionar participante
              </Button>}
            </section>

            <section className={styles.gerenciarPresencaPanel}>
              <div className={styles.gerenciarPresencaHeader}>
                <KeyboardArrowRight className={styles.gerenciarPresencaArrow} />
                <strong>Gerenciar critérios de aprovação</strong>
              </div>
              {exibirGerenciamentoPresenca && (
                <div className={styles.gerenciarPresencaContent}>
                  <div className={styles.presencaLancamentoBox}>
                    <h3>Lançamento Externo</h3>
                    <div className={styles.presencaOptionGrid}>
                      {canGerarLink && <button type="button" className={styles.presencaOptionCard} onClick={handleOpenQrCode}>
                        <QrCode2 className={styles.presencaOptionIcon} />
                        <span className={styles.titleButtonOption}>Gerar QrCode</span>
                        <span className={styles.subtitleButtonOption}>Leitura via celular</span>
                      </button>}

                      {canGerarLink && <button type="button" className={styles.presencaOptionCard} onClick={handleOpenGerarLink}>
                        <span className={styles.presencaOptionCircle}>
                          <LinkIcon />
                        </span>
                        <span className={styles.titleButtonOption}>Gerar Link</span>
                        <span className={styles.subtitleButtonOption}>Link específico</span>
                      </button>}
                    </div>

                    {canGerarLink && <Button
                      className={styles.encerrarPresencaButton}
                      onClick={() => setOpenEncerrarLink(true)}
                    >
                      Encerrar QrCode ou Link
                    </Button>}
                  </div>

                  <div className={styles.presencaLancamentoBox}>
                    <h3>Lançamento Interno</h3>
                    <div className={styles.presencaOptionGrid}>
                      {canLancarPresenca && <button type="button" className={styles.presencaOptionCard} onClick={handleOpenLancamentoManual}>
                        <DriveFileRenameOutline className={styles.presencaOptionIconDark} />
                        <span className={styles.titleButtonOption}>Lançamento Manual</span>
                        <span className={styles.subtitleButtonOption}>Marcar presente/ausente</span>
                      </button>}

                      {canLancarPresenca && <button type="button" className={styles.presencaOptionCard} onClick={handleOpenUploadCsv}>
                        <CloudUpload className={styles.presencaOptionIcon} />
                        <span className={styles.titleButtonOption}>Upload de CSV</span>
                        <span className={styles.subtitleButtonOption}>Lista de Presença</span>
                      </button>}
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.notasLancamentoContent}>
                {exibirLancamentoNotas && (
                  <div className={styles.presencaLancamentoBox}>
                    <h3>Notas da atividade avaliativa</h3>
                    <div className={styles.presencaOptionGrid}>
                      {canLancarNotas && <button type="button" className={styles.presencaOptionCard} onClick={handleOpenLancamentoNotasManual}>
                        <DriveFileRenameOutline className={styles.presencaOptionIconDark} />
                        <span className={styles.titleButtonOption}>Lançamento Manual</span>
                        <span className={styles.subtitleButtonOption}>Informar notas</span>
                      </button>}

                      {canLancarNotas && <button type="button" className={styles.presencaOptionCard} onClick={handleOpenUploadNotasCsv}>
                        <CloudUpload className={styles.presencaOptionIcon} />
                        <span className={styles.titleButtonOption}>Upload de CSV</span>
                        <span className={styles.subtitleButtonOption}>Lista de notas</span>
                      </button>}
                    </div>
                  </div>
                )}
                {canProcessarResultados && 
                  <div className={styles.processarResultadoBox}>
                    <h3>Resultado da turma</h3>
                    <p>Processar resultados considerando o critério de aprovação configurado.</p>
                    
                      <Button
                        className={styles.lancamentoManualButtonGreen}
                        startIcon={<CheckCircle />}
                        onClick={() => setOpenConfirmProcessarResultados(true)}
                        disabled={loadingProcessarResultados}
                      >
                        Processar resultados
                      </Button>
                  </div>
                }
                </div>
            </section>

            {linkFeedback && (
              <Alert severity="success" className={styles.presencaFeedback}>
                {linkFeedback}
              </Alert>
            )}

            

            <div className={styles_filter.container}>
              <div className={styles_filter.grid12}>
                <div className={styles_filter.inputGroup4}>
                  <TextField
                    size="small"
                    variant="standard"
                    fullWidth
                    placeholder="Nome"
                    value={nomeFiltroInscrito}
                    onChange={(event) => {
                      setNomeFiltroInscrito(event.target.value);
                      setCurrentPage(1);
                    }}
                    InputProps={{ className: styles_input.input, disableUnderline: true }}
                  />
                </div>

                <div className={styles_filter.inputGroup4}>
                  <TextField
                    size="small"
                    variant="standard"
                    fullWidth
                    placeholder="E-mail"
                    value={emailFiltroInscrito}
                    onChange={(event) => {
                      setEmailFiltroInscrito(event.target.value);
                      setCurrentPage(1);
                    }}
                    InputProps={{ className: styles_input.input, disableUnderline: true }}
                  />
                </div>

                <div className={styles_filter.inputGroup4}>
                  <TextField
                    size="small"
                    variant="standard"
                    fullWidth
                    placeholder="Municipio"
                    value={municipioFiltroInscrito}
                    onChange={(event) => {
                      setMunicipioFiltroInscrito(event.target.value);
                      setCurrentPage(1);
                    }}
                    InputProps={{ className: styles_input.input, disableUnderline: true }}
                  />
                </div>

                <div className={styles_filter.inputGroup3}>
                  <CpfField
                    size="small"
                    placeholder="CPF"
                    variant="standard"
                    autoFocus={false}
                    value={cpfFiltroInscrito}
                    onUpdate={(value) => {
                      setCpfFiltroInscrito(value);
                      setCurrentPage(1);
                    }}
                    customClassName={styles_input.input}
                  />
                </div>

                <div className={styles_filter.inputGroup3}>
                  <SelectField
                    label=""
                    placeholder="Âmbito"
                    value={getAmbitoFiltroLabel(ambitoFiltroInscrito)}
                    onChange={(value) => {
                      setAmbitoFiltroInscrito(value);
                      setCurrentPage(1);
                    }}
                    options={[{ nome: "Estadual" }, { nome: "Municipal" }]}
                    disableTyping
                  />
                </div>

                <div className={styles_filter.inputGroup3}>
                  <SelectField
                    label=""
                    placeholder="Situação"
                    value={getSituacaoFiltroLabel(situacaoFiltroInscrito)}
                    onChange={(value) => {
                      if (value === "Aprovado") {
                        setSituacaoFiltroInscrito("APROVADO");
                      } else if (value === "Reprovado") {
                        setSituacaoFiltroInscrito("REPROVADO");
                      } else {
                        setSituacaoFiltroInscrito("");
                      }
                      setCurrentPage(1);
                    }}
                    options={[{ nome: "Aprovado" }, { nome: "Reprovado" }]}
                    disableTyping
                  />
                </div>

                <div className={styles_filter.inputGroup3}>
                  <div className={styles_filter.containerButton}>
                    <div className={styles_filter.lineButton}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setNomeFiltroInscrito("");
                          setCpfFiltroInscrito("");
                          setEmailFiltroInscrito("");
                          setMunicipioFiltroInscrito("");
                          setAmbitoFiltroInscrito("");
                          setLotacaoOrgaoFiltroInscrito("");
                          setSituacaoFiltroInscrito("");
                          setCurrentPage(1);
                        }}
                        className={styles_button.buttonSquareSecondary}
                      >
                        Limpar filtros
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedInscritos.size > 0 && canExcluirInscrito && (
              <div className={styles_table.actionsMass}>
                <MultiActionButton
                  buttonLabel="Ações em Massa"
                  actions={[
                    {
                      label: "Excluir Usuários",
                      icon: <DeleteOutline />,
                      onClick: () => setOpenConfirmCancelarInscricoesMassa(true),
                    },
                  ]}
                />
              </div>
            )}

            <TableContainer className={styles.presencaTableContainer}>
              {loadingInscritos && (
                <div className={styles.tableLoading}>
                  <CircularProgress size={24} />
                </div>
              )}
              <div className={styles.presencaDesktopTable}>
              <Table size="small">
                <TableHead className={styles_table.tableHead}>
                  <TableRow>
                    <TableCell className={styles_table.tableHeadRow} padding="none" align="center">
                      <Checkbox
                        {...checkboxLabel}
                        checked={allInscritosSelected}
                        indeterminate={!allInscritosSelected && someInscritosSelected}
                        onChange={handleSelectAllInscritos}
                        sx={{
                          color: "#FFFFFF",
                          "&.Mui-checked": { color: "#FFFFFF" },
                          "&.MuiCheckbox-indeterminate": { color: "#FFFFFF" },
                        }}
                      />
                    </TableCell>
                    <TableCell className={styles_table.tableHeadRow}>Nome do participante</TableCell>
                    <TableCell className={styles_table.tableHeadRow} align="center">CPF</TableCell>
                    <TableCell className={styles_table.tableHeadRow} align="center">Município de Atuação</TableCell>
                    <TableCell className={styles_table.tableHeadRow} align="center">Âmbito de Atuação</TableCell>
                    <TableCell className={styles_table.tableHeadRow} align="center">Lotação / Órgão</TableCell>
                    <TableCell className={styles_table.tableHeadRow}>E-mail</TableCell>
                    {/*<TableCell className={styles_table.tableHeadRow} align="center">Nota</TableCell>*/}
                    <TableCell className={styles_table.tableHeadRow} align="center">Data/hora de inscrição</TableCell>
                    <TableCell className={styles_table.tableHeadRow} align="center">Situação</TableCell>
                    <TableCell className={styles_table.tableHeadRow} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inscritos.length === 0 && !loadingInscritos && (
                    <TableRow>
                      <TableCell className={styles_table.textXs} align="center" colSpan={10}>
                        Nenhum inscrito encontrado.
                      </TableCell>
                    </TableRow>
                  )}

                  {inscritos.map((inscrito, index) => (
                    <TableRow
                      key={inscrito.id}
                      className={`${styles_table.tableRow} ${
                        index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd
                      }`}
                    >
                      <TableCell className={styles_table.textXs} align="center" padding="none">
                        <Checkbox
                          {...checkboxLabel}
                          checked={selectedInscritos.has(String(inscrito.id))}
                          onChange={() => handleInscritoSelection(inscrito.id)}
                        />
                      </TableCell>
                      <TableCell className={styles_table.textXs}><strong>{inscrito.nome}</strong></TableCell>
                      <TableCell className={styles_table.textXs} align="center">{formatarCpf(inscrito.cpf)}</TableCell>
                      <TableCell className={styles_table.textXs} align="center">{inscrito.inscricao?.municipio_nome ?? "-"}</TableCell>
                      <TableCell className={styles_table.textXs} align="center">{inscrito.inscricao?.ambito_nome ?? "-"}</TableCell>
                      <TableCell className={styles_table.textXs} align="center">
                        {inscrito.dados_profissionais?.lotacao_nome ?? inscrito.dados_profissionais?.orgao_nome}
                      </TableCell>
                      <TableCell className={styles_table.textXs}>{inscrito.email}</TableCell>
                      {/*<TableCell className={styles_table.textXs} align="center">
                        {inscrito.nota?.nota !== null && inscrito.nota?.nota !== undefined ? inscrito.nota.nota : "-"}
                      </TableCell>*/}
                      <TableCell className={styles_table.textXs} align="center">{formatarDataHora(inscrito.inscricao?.created_at)}</TableCell>
                      <TableCell className={styles_table.textXs} align="center">
                        {inscrito.resultado?.status ? (
                          <span
                            className={`${styles.resultadoBadge} ${
                              inscrito.resultado.status === "APROVADO"
                                ? styles.resultadoAprovado
                                : styles.resultadoReprovado
                            }`}
                          >
                            {inscrito.resultado.status}
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell className={styles_table.textXs} align="center">
                        <div className={styles_table.blockActions}>
                          <IconButton
                            aria-label="Visualizar Presenças do inscrito"
                            size="small"
                            className={styles.presencaEditAction}
                            onClick={() => handleOpenVisualizarPresencas(inscrito)}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                          {canEditarInscrito && <IconButton
                            aria-label="Editar dados do inscrito"
                            size="small"
                            className={styles.presencaEditAction}
                            onClick={() => handleEditarDadosInscrito(inscrito)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>}
                          {canExcluirInscrito && <IconButton
                            aria-label="Cancelar inscrição do inscrito"
                            size="small"
                            className={styles.presencaRemoveAction}
                            onClick={() => handleOpenCancelarInscricao(inscrito)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>

              <div className={styles.presencaMobileList}>
                {inscritos.length === 0 && !loadingInscritos && (
                  <div className={styles.presencaMobileEmpty}>Nenhum inscrito encontrado.</div>
                )}

                {inscritos.map((inscrito) => (
                  <div key={inscrito.id} className={styles.presencaMobileCard}>
                    <div className={styles.presencaMobileHeader}>
                      <strong>{inscrito.nome}</strong>
                      {inscrito.resultado?.status ? (
                        <span
                          className={`${styles.resultadoBadge} ${
                            inscrito.resultado.status === "APROVADO"
                              ? styles.resultadoAprovado
                              : styles.resultadoReprovado
                          }`}
                        >
                          {inscrito.resultado.status}
                        </span>
                      ) : (
                        <span className={styles.presencaMobileStatusMuted}>-</span>
                      )}
                    </div>

                    <div className={styles.presencaMobileGrid}>
                      <div className={styles.presencaMobileItem}>
                        <span>CPF</span>
                        <strong>{formatarCpf(inscrito.cpf)}</strong>
                      </div>
                      <div className={styles.presencaMobileItem}>
                        <span>inscrição</span>
                        <strong>{formatarDataHora(inscrito.inscricao?.created_at)}</strong>
                      </div>
                      <div className={styles.presencaMobileItem}>
                        <span>Município</span>
                        <strong>{inscrito.inscricao?.municipio_nome ?? "-"}</strong>
                      </div>
                      <div className={styles.presencaMobileItem}>
                        <span>Âmbito</span>
                        <strong>{inscrito.inscricao?.ambito_nome ?? "-"}</strong>
                      </div>
                      <div className={styles.presencaMobileItem}>
                        <span>Lotação / Órgão</span>
                        <strong>{inscrito.dados_profissionais?.lotacao_nome ?? inscrito.dados_profissionais?.orgao_nome ?? "-"}</strong>
                      </div>
                      <div className={styles.presencaMobileItem}>
                        <span>E-mail</span>
                        <strong className={styles.presencaMobileEmail}>{inscrito.email}</strong>
                      </div>
                    </div>

                    <div className={styles.presencaMobileActions}>
                      <IconButton
                        aria-label="Visualizar Presenças do inscrito"
                        size="small"
                        className={styles.presencaEditAction}
                        onClick={() => handleOpenVisualizarPresencas(inscrito)}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                      {canEditarInscrito && <IconButton
                        aria-label="Editar dados do inscrito"
                        size="small"
                        className={styles.presencaEditAction}
                        onClick={() => handleEditarDadosInscrito(inscrito)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>}
                      {canExcluirInscrito && <IconButton
                        aria-label="Cancelar inscrição do inscrito"
                        size="small"
                        className={styles.presencaRemoveAction}
                        onClick={() => handleOpenCancelarInscricao(inscrito)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>}
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles_table.containerPagination}>
                <div className={styles_table.qtdItems}>
                  <label className={styles_table.labelSelect}>Resultados por página</label>
                  <select
                    value={perPage.toString()}
                    onChange={handleChangePerPage}
                    className={styles.presencaSelect}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                <div className={styles_table.pagination}>
                  <Button
                    className={currentPage <= 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                  >
                    <KeyboardArrowLeft />
                  </Button>
                  <span className={styles_table.pages}>página {currentPage} de {totalPages}</span>
                  <Button
                    className={currentPage >= totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                  >
                    <KeyboardArrowRight />
                  </Button>
                </div>
              </div>
            </TableContainer>

            <div className={styles.backActions}>
              <Button
                variant="contained"
                onClick={handleBack}
                className={styles_button.buttonSecondary}
                color="primary"
              >
                Voltar
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog
        open={openAdicionarParticipante}
        onClose={() => !loadingAdicionarParticipante && setOpenAdicionarParticipante(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Adicionar Participante</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton
            onClick={() => setOpenAdicionarParticipante(false)}
            className={styles.presencaModalClose}
            disabled={loadingAdicionarParticipante}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.presencaModalContent}>
          <div className={styles_filter.container}>
            <div className={styles_filter.grid12}>
              <div className={styles_filter.inputGroup4}>
                <label className={styles_input.titleInput}>Nome Completo</label>
                <TextField
                  size="small"
                  placeholder=""
                  variant="standard"
                  autoFocus={false}
                  value={nomeFiltroUsuario}
                  onChange={(event) => setNomeFiltroUsuario(event.target.value)}
                  fullWidth
                  InputProps={{
                    className: styles_input.input,
                  }}
                  sx={{
                    "& .MuiInput-underline:before": { borderBottom: "none" },
                    "& .MuiInput-underline:after": { borderBottom: "none" },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                  }}
                />
              </div>

              <div className={styles_filter.inputGroup3}>
                <label className={styles_input.titleInput}>CPF</label>
                <CpfField
                  size="small"
                  placeholder=""
                  variant="standard"
                  autoFocus={false}
                  value={cpfFiltroUsuario}
                  onUpdate={(value) => setCpfFiltroUsuario(value)}
                  customClassName={styles_input.input}
                />
              </div>

              <div className={styles_filter.inputGroup5}>
                <label className={styles_input.titleInput}>E-mail</label>
                <TextField
                  size="small"
                  placeholder=""
                  variant="standard"
                  autoFocus={false}
                  value={emailFiltroUsuario}
                  onChange={(event) => setEmailFiltroUsuario(event.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 150 }}
                  InputProps={{
                    className: styles_input.input,
                  }}
                  sx={{
                    "& .MuiInput-underline:before": { borderBottom: "none" },
                    "& .MuiInput-underline:after": { borderBottom: "none" },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                  }}
                />
              </div>

              <div className={styles_filter.inputGroupFull}>
                <div className={styles.buscarUsuarioActions}>
            <Button
              variant="contained"
              className={styles_filter.buttonClean}
              onClick={handleLimparBuscaUsuarios}
              disabled={loadingBuscarUsuarios}
            >
              Limpar
            </Button>
            <Button
              variant="contained"
              className={styles_filter.buttonSearch}
              onClick={() => handleBuscarUsuarios(1)}
              disabled={loadingBuscarUsuarios || !temFiltroBuscaUsuario()}
            >
              {loadingBuscarUsuarios ? (
                <>
                  <div className={styles_button.spinner}></div>
                  <span>Pesquisando...</span>
                </>
              ) : (
                <span>Pesquisar</span>
              )}
            </Button>
            {canCadastrarAdicionarInscrito && <Button
              variant="contained"
              className={styles_button.buttonSquareSuccess}
              onClick={handleCadastrarNovoParticipante}
              disabled={!turmaId}
            >
              Cadastrar novo usuário
            </Button>}
                </div>
              </div>
            </div>
          </div>

          {usuariosBuscaError && (
            <Alert severity="error" className={styles.uploadCsvAlert}>
              {usuariosBuscaError}
            </Alert>
          )}

          <div className={styles.presencaModalDivider} />

          {!buscaUsuariosExecutada && !loadingBuscarUsuarios && (
            <div className={styles.buscarUsuarioEmpty}>
              Preencha pelo menos um filtro para listar usuários.
            </div>
          )}

          {loadingBuscarUsuarios && (
            <div className={styles.lancamentoManualLoading}>
              <CircularProgress size={24} />
            </div>
          )}

          {buscaUsuariosExecutada && !loadingBuscarUsuarios && (
            <>
              <TableContainer className={styles.buscarUsuarioTable}>
                <Table size="small">
                  <TableHead className={styles_table.tableHead2}>
                    <TableRow>
                      <TableCell className={styles_table.tableHeadRow2}>Nome</TableCell>
                      <TableCell className={styles_table.tableHeadRow2} align="center">CPF</TableCell>
                      <TableCell className={styles_table.tableHeadRow2}>E-mail</TableCell>
                      <TableCell className={styles_table.tableHeadRow2} align="center">Município</TableCell>
                      <TableCell className={styles_table.tableHeadRow2} align="center">Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usuariosEncontrados.length === 0 && (
                      <TableRow>
                        <TableCell className={styles_table.textXs} align="center" colSpan={5}>
                          Nenhum usuário encontrado.
                        </TableCell>
                      </TableRow>
                    )}

                    {usuariosEncontrados.map((usuarioBusca) => (
                      <TableRow key={usuarioBusca.id}>
                        <TableCell className={styles_table.textXs}>
                          <strong>{usuarioBusca.nome}</strong>
                        </TableCell>
                        <TableCell className={styles_table.textXs} align="center">
                          {formatarCpf(usuarioBusca.cpf)}
                        </TableCell>
                        <TableCell className={styles_table.textXs}>{usuarioBusca.email}</TableCell>
                        <TableCell className={styles_table.textXs} align="center">
                          {usuarioBusca.municipio || "-"}
                        </TableCell>
                        <TableCell className={styles_table.textXs} align="center">
                          <Button
                            variant="contained"
                            className={styles_button.buttonSquareSuccess}
                            startIcon={<AddCircleOutline />}
                            onClick={() => handleOpenConfirmAdicionarParticipante(usuarioBusca)}
                          >
                            Matricular
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <div className={styles.buscarUsuarioPagination}>
                <Button
                  className={usuariosBuscaPage <= 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                  disabled={usuariosBuscaPage <= 1 || loadingBuscarUsuarios}
                  onClick={() => handleBuscarUsuarios(Math.max(usuariosBuscaPage - 1, 1))}
                >
                  <KeyboardArrowLeft />
                </Button>
                <span className={styles_table.pages}>página {usuariosBuscaPage} de {usuariosBuscaTotalPages}</span>
                <Button
                  className={usuariosBuscaPage >= usuariosBuscaTotalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
                  disabled={usuariosBuscaPage >= usuariosBuscaTotalPages || loadingBuscarUsuarios}
                  onClick={() => handleBuscarUsuarios(Math.min(usuariosBuscaPage + 1, usuariosBuscaTotalPages))}
                >
                  <KeyboardArrowRight />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={openExportar}
        loading={loadingXls}
        onClose={() => setOpenExportar(false)}
        onConfirm={handleExportarInscritos}
        title="Exportar inscritos em XLS"
        message="Deseja confirmar a exportação dos inscritos em XLS?"
        titleButtonConfirm="Exportar"
        titleLoadButton="Exportando..."
      />

      <Dialog open={openListaPresenca} onClose={() => setOpenListaPresenca(false)}>
        <DialogTitle>Gerar Lista de Presença</DialogTitle>
        <DialogContent>
          <label className={styles.listaPresencaLabel}>Selecione o dia da turma</label>
          <Select
            value={dataListaPresenca}
            onChange={(event) => setDataListaPresenca(event.target.value)}
            fullWidth
            size="small"
          >
            {diasTurma.map((dia) => (
              <MenuItem key={dia} value={dia}>
                {formatarDataDbParaBr(dia)}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button
            className={styles_button.buttonOutlineDanger}
            onClick={() => setOpenListaPresenca(false)}
            disabled={loadingPdf}
          >
            Cancelar
          </Button>
          <Button
            className={`${styles_button.buttonPrimary} ${loadingPdf ? styles_button.buttonLoad : ""}`}
            onClick={handleGerarListaPresenca}
            disabled={loadingPdf || !dataListaPresenca}
          >
            {loadingPdf ? (
              <>
                <div className={styles_button.spinner}></div>
                <span>Gerando...</span>
              </>
            ) : (
              <span>Gerar PDF</span>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUploadCsv}
        onClose={() => {
          if (loadingUploadCsv) return;
          setOpenUploadCsv(false);
          setUploadCsvResult(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Upload de CSV</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton
            onClick={() => {
              setOpenUploadCsv(false);
              setUploadCsvResult(null);
            }}
            className={styles.presencaModalClose}
            disabled={loadingUploadCsv}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.presencaModalContent}>
          <p className={styles.uploadCsvDescription}>
            Envie uma planilha no formato CSV para registrar a Presença dos participantes dessa turma. O arquivo deve seguir o modelo fornecido para evitar problemas no Lançamento.
          </p>

          <div
            className={`${styles.uploadCsvDropzone} ${draggingCsv ? styles.uploadCsvDropzoneActive : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDraggingCsv(true);
            }}
            onDragLeave={() => setDraggingCsv(false)}
            onDrop={handleDropArquivoCsvPresenca}
          >
            <CloudUpload className={styles.uploadCsvIcon} />
            <span>Selecione o arquivo ou arraste-o aqui para fazer upload (.csv)</span>
            <div className={styles.uploadCsvFileRow}>
              <input
                id="upload-presencas-csv"
                type="file"
                accept=".csv"
                className={styles.uploadCsvInput}
                onChange={handleChangeArquivoCsvPresenca}
                disabled={loadingUploadCsv}
              />
              <label htmlFor="upload-presencas-csv" className={styles.uploadCsvFileButton}>
                <DriveFileRenameOutline />
                Escolher arquivo
              </label>
              <div className={styles.uploadCsvFileName}>
                {arquivoCsvPresenca?.name || "Nenhum arquivo selecionado"}
              </div>
            </div>
          </div>

          <ul className={styles.uploadCsvRules}>
            <li>Somente arquivos do tipo .csv são permitidos.</li>
            <li>O arquivo CSV deve ser separado por ponto e vírgula (;).</li>
            <li>Tamanho máximo: 5 MB.</li>
          </ul>

          {uploadCsvError && (
            <Alert severity="error" className={styles.uploadCsvAlert}>
              {uploadCsvError}
            </Alert>
          )}

          {renderImportResult(uploadCsvResult, "presencas")}

          <button type="button" className={styles.uploadCsvDownload} onClick={baixarModeloCsvPresenca}>
            <Description />
            Baixar modelo de CSV
          </button>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button
              className={styles.presencaModalButtonOutline}
              onClick={() => {
                setOpenUploadCsv(false);
                setUploadCsvResult(null);
              }}
              disabled={loadingUploadCsv}
            >
              Cancelar
            </Button>
            <Button
              className={styles.presencaModalButtonGreen}
              startIcon={loadingUploadCsv ? <CircularProgress size={16} /> : <Send />}
              onClick={handleEnviarUploadCsv}
              disabled={loadingUploadCsv || !arquivoCsvPresenca}
            >
              Enviar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openUploadNotasCsv}
        onClose={() => {
          if (loadingUploadNotasCsv) return;
          setOpenUploadNotasCsv(false);
          setUploadNotasCsvResult(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Upload de CSV de Notas</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton
            onClick={() => {
              setOpenUploadNotasCsv(false);
              setUploadNotasCsvResult(null);
            }}
            className={styles.presencaModalClose}
            disabled={loadingUploadNotasCsv}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.presencaModalContent}>
          <p className={styles.uploadCsvDescription}>
            Envie uma planilha no formato CSV para lançar as notas dos participantes dessa turma. O arquivo deve conter CPF e nota.
          </p>

          <div
            className={`${styles.uploadCsvDropzone} ${draggingNotasCsv ? styles.uploadCsvDropzoneActive : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDraggingNotasCsv(true);
            }}
            onDragLeave={() => setDraggingNotasCsv(false)}
            onDrop={handleDropArquivoCsvNotas}
          >
            <CloudUpload className={styles.uploadCsvIcon} />
            <span>Selecione o arquivo ou arraste-o aqui para fazer upload (.csv)</span>
            <div className={styles.uploadCsvFileRow}>
              <input
                id="upload-notas-csv"
                type="file"
                accept=".csv"
                className={styles.uploadCsvInput}
                onChange={handleChangeArquivoCsvNotas}
                disabled={loadingUploadNotasCsv}
              />
              <label htmlFor="upload-notas-csv" className={styles.uploadCsvFileButton}>
                <DriveFileRenameOutline />
                Escolher arquivo
              </label>
              <div className={styles.uploadCsvFileName}>
                {arquivoCsvNotas?.name || "Nenhum arquivo selecionado"}
              </div>
            </div>
          </div>

          <ul className={styles.uploadCsvRules}>
            <li>Somente arquivos do tipo .csv são permitidos.</li>
            <li>O arquivo CSV deve ser separado por ponto e vírgula (;).</li>
            <li>Para notas decimais, use ponto (.) como separador decimal, e não vírgula.</li>
            <li>Tamanho máximo: 5 MB.</li>
          </ul>

          {uploadNotasCsvError && (
            <Alert severity="error" className={styles.uploadCsvAlert}>
              {uploadNotasCsvError}
            </Alert>
          )}

          {renderImportResult(uploadNotasCsvResult, "notas")}

          <button type="button" className={styles.uploadCsvDownload} onClick={baixarModeloCsvNotas}>
            <Description />
            Baixar modelo de CSV
          </button>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button
              className={styles.presencaModalButtonOutline}
              onClick={() => {
                setOpenUploadNotasCsv(false);
                setUploadNotasCsvResult(null);
              }}
              disabled={loadingUploadNotasCsv}
            >
              Cancelar
            </Button>
            <Button
              className={styles.presencaModalButtonGreen}
              startIcon={loadingUploadNotasCsv ? <CircularProgress size={16} /> : <Send />}
              onClick={handleEnviarUploadNotasCsv}
              disabled={loadingUploadNotasCsv || !arquivoCsvNotas}
            >
              Enviar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openLancamentoNotasManual}
        onClose={() => setOpenLancamentoNotasManual(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Lançamento Manual de Notas</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton onClick={() => setOpenLancamentoNotasManual(false)} className={styles.presencaModalClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.presencaModalContent}>
          <div className={styles.lancamentoManualTable}>
            <div className={styles.lancamentoNotasTableHeader}>
              <span>Nome</span>
              <span>CPF</span>
              <span>Município</span>
              <span>Nota</span>
            </div>

            {loadingLancamentoNotas && (
              <div className={styles.lancamentoManualLoading}>
                <CircularProgress size={24} />
              </div>
            )}

            {!loadingLancamentoNotas && inscritosLancamentoNotas.length === 0 && (
              <div className={styles.lancamentoManualEmpty}>Nenhum inscrito encontrado.</div>
            )}

            {!loadingLancamentoNotas && inscritosLancamentoNotas.map((inscrito, index) => {
              const loadingUsuario = loadingNotaManualUsuario === inscrito.id;

              return (
                <div className={styles.lancamentoNotasRow} key={inscrito.id}>
                  <div className={styles.lancamentoManualNome}>
                    <span className={`${styles.lancamentoManualAvatar} ${styles[`avatarColor${index % 5}`]}`}>
                      {getIniciaisNome(inscrito.nome)}
                    </span>
                    <strong>{inscrito.nome}</strong>
                  </div>
                  <span>{mascararCpfCompacto(inscrito.cpf)}</span>
                  <span>{inscrito.inscricao?.municipio_nome ?? inscrito.dados_profissionais?.municipio_nome ?? "-"}</span>
                  <div className={styles.notaManualActions}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={notasLancamentoManual[inscrito.id] ?? ""}
                      onChange={(event) => handleChangeNotaManual(inscrito.id, event.target.value)}
                      className={styles.notaManualInput}
                      placeholder="0"
                      disabled={loadingUsuario}
                      aria-label={`Nota de ${inscrito.nome}`}
                    />
                    <Button
                      className={styles.notaManualSaveButton}
                      startIcon={loadingUsuario ? <CircularProgress size={14} /> : <Save />}
                      onClick={() => handleSalvarNotaManual(inscrito)}
                      disabled={loadingUsuario}
                    >
                      Salvar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button className={styles.presencaModalButtonOutline} onClick={() => setOpenLancamentoNotasManual(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openQrCode}
        onClose={() => setOpenQrCode(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>QR Code de Presença</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton onClick={() => setOpenQrCode(false)} className={styles.presencaModalClose}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className={styles.presencaModalContent}>
          <p>Escaneie o QR Code para registrar Presença nesta turma.</p>

          <div className={styles.qrCodePreview}>
            {loadingLinkPresenca ? <CircularProgress /> : qrCodeUrl && <img src={qrCodeUrl} alt="QR Code de Presença" />}
          </div>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button
              className={styles.presencaModalButtonBlue}
              startIcon={<FileDownload />}
              onClick={baixarQrCode}
              disabled={loadingLinkPresenca || !qrCodeUrl}
            >
              Baixar QR Code
            </Button>
            <Button
              className={styles.presencaModalButtonOutline}
              startIcon={<Close />}
              onClick={() => setOpenQrCode(false)}
            >
              Fechar
            </Button>
          </div>

          <div className={styles.presencaModalInfo}>
            <InfoOutlined />
            <span>O QR Code gerado é unico e válido apenas para esta turma.</span>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openGerarLink}
        onClose={() => setOpenGerarLink(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Link para Registro de Presença</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton onClick={() => setOpenGerarLink(false)} className={styles.presencaModalClose}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className={styles.presencaModalContent}>
          <div className={styles.linkPresencaField}>
            <LinkIcon />
            {loadingLinkPresenca ? (
              <span>Gerando link...</span>
            ) : (
              <input readOnly value={presencaUrl} aria-label="Link para registro de Presença" />
            )}
          </div>

          <div className={styles.linkPresencaTopAction}>
            <Button
              className={styles.presencaModalButtonBlue}
              startIcon={<LinkIcon />}
              onClick={copiarLinkPresenca}
              disabled={loadingLinkPresenca || !presencaUrl}
            >
              Copiar link
            </Button>
          </div>

          <div className={styles.presencaModalDivider} />

          <p className={styles.linkPresencaDescription}>
            Este é o link gerado para os participantes registrarem Presença em turma {linkPresenca?.turma_id || turma?.id}.
            <br />
            O link é único e válido apenas para esta turma.
          </p>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button className={styles.presencaModalButtonOutline} onClick={() => setOpenGerarLink(false)}>
              Fechar
            </Button>
            {/*<Button
              className={styles.presencaModalButtonGreen}
              startIcon={<Send />}
              onClick={copiarLinkPresenca}
              disabled={loadingLinkPresenca || !presencaUrl}
            >
              Copiar link
            </Button>*/}
          </div>

          <div className={styles.presencaModalInfo}>
            <ContentCopy />
            <span>O link gerado é único e válido apenas para esta turma.</span>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openLancamentoManual}
        onClose={() => setOpenLancamentoManual(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Lançamento Manual de Presença</span>
          <small>{turma?.nome || "-"}</small>
          <IconButton onClick={() => setOpenLancamentoManual(false)} className={styles.presencaModalClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.presencaModalContent}>
          <div className={styles.lancamentoManualTopBar}>
            <div className={styles.lancamentoManualDia}>
              <label>Dia da turma</label>
              <Select
                value={dataLancamentoManual}
                onChange={(event) => setDataLancamentoManual(event.target.value)}
                size="small"
                fullWidth
              >
                {diasTurma.map((dia) => (
                  <MenuItem key={dia} value={dia}>
                    {formatarDataDbParaBr(dia)}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <Button
              className={styles.lancamentoManualButtonLightGreen}
              startIcon={<CheckCircle />}
              onClick={() => setOpenConfirmMarcarTodosPresentes(true)}
              disabled={!dataLancamentoManual || loadingLancamentoManual}
            >
              Marcar todos como presentes
            </Button>
            <Button
              className={styles.lancamentoManualButtonLight}
              startIcon={<Cancel />}
              onClick={() => setOpenConfirmLimparMarcacoes(true)}
              disabled={!dataLancamentoManual || loadingLancamentoManual}
            >
              Limpar marcações
            </Button>
            {/*
            <Button
              className={styles.lancamentoManualButtonGreen}
              startIcon={<Save />}
              onClick={() => setOpenConfirmSalvarPresencas(true)}
              disabled={!dataLancamentoManual || loadingLancamentoManual}
            >
              Salvar Presença
            </Button>
            */}
          </div>

          <div className={styles.presencaModalDivider} />

          <div className={styles.lancamentoManualTable}>
            <div className={styles.lancamentoManualTableHeader}>
              <span>Nome</span>
              <span>CPF</span>
              <span>Município</span>
              <span>Presença</span>
            </div>

            {loadingLancamentoManual && (
              <div className={styles.lancamentoManualLoading}>
                <CircularProgress size={24} />
              </div>
            )}

            {!loadingLancamentoManual && inscritosLancamentoManual.length === 0 && (
              <div className={styles.lancamentoManualEmpty}>Nenhum inscrito encontrado.</div>
            )}

            {!loadingLancamentoManual && inscritosLancamentoManual.map((inscrito, index) => {
              const statusPresenca = presencasLancamentoManual[inscrito.id] ?? "nao_registrado";
              const loadingUsuario = loadingPresencaManualUsuario === inscrito.id;

              return (
                <div className={styles.lancamentoManualRow} key={inscrito.id}>
                  <div className={styles.lancamentoManualNome}>
                    <span className={`${styles.lancamentoManualAvatar} ${styles[`avatarColor${index % 5}`]}`}>
                      {getIniciaisNome(inscrito.nome)}
                    </span>
                    <strong>{inscrito.nome}</strong>
                  </div>
                  <span>{mascararCpfCompacto(inscrito.cpf)}</span>
                  <span>{inscrito.inscricao?.municipio_nome ?? inscrito.dados_profissionais?.municipio_nome ?? "-"}</span>
                  <div className={styles.lancamentoManualPresenceGroup}>
                    <Button
                      className={
                        statusPresenca === "presente"
                          ? styles.lancamentoManualPresenceActive
                          : styles.lancamentoManualPresenceButton
                      }
                      startIcon={loadingUsuario ? <CircularProgress size={14} /> : <Check />}
                      onClick={() => handleMarcarPresencaManual(inscrito, "presente")}
                      disabled={loadingUsuario}
                    >
                      Presente
                    </Button>
                    <Button
                      className={
                        statusPresenca === "ausente"
                          ? styles.lancamentoManualAbsenceActive
                          : styles.lancamentoManualPresenceButton
                      }
                      startIcon={<Close />}
                      onClick={() => handleMarcarPresencaManual(inscrito, "ausente")}
                      disabled={loadingUsuario}
                    >
                      Ausente
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.lancamentoManualLegend}>
            <span><CheckCircle className={styles.legendPresent} /> Presente</span>
            <span><Cancel className={styles.legendAbsent} /> Ausente</span>
            <span><RemoveCircle className={styles.legendNone} /> não registrado</span>
          </div>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button className={styles.presencaModalButtonOutline} onClick={() => setOpenLancamentoManual(false)}>
              Cancelar
            </Button>
            <Button
              className={styles.presencaModalButtonGreen}
              startIcon={<Save />}
              onClick={() => setOpenConfirmSalvarPresencas(true)}
              disabled={!dataLancamentoManual || loadingLancamentoManual}
            >
              Salvar Presença
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openVisualizarPresencas}
        onClose={() => {
          setOpenVisualizarPresencas(false);
          setInscritoSelecionadoPresencas(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: styles.presencaModalPaper }}
      >
        <DialogTitle className={styles.presencaModalTitle}>
          <span>Presenças do participante</span>
          <small>{inscritoSelecionadoPresencas?.nome || "-"}</small>
          <IconButton
            onClick={() => {
              setOpenVisualizarPresencas(false);
              setInscritoSelecionadoPresencas(null);
            }}
            className={styles.presencaModalClose}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className={styles.presencaModalContent}>
          <TableContainer className={styles.presencaTableContainer}>
            <Table size="small">
              <TableHead className={styles_table.tableHead2}>
                <TableRow>
                  <TableCell className={styles_table.tableHeadRow2} align="center">Data da presença</TableCell>
                  <TableCell className={styles_table.tableHeadRow2} align="center">Tipo</TableCell>
                  <TableCell className={styles_table.tableHeadRow2} align="center">Origem</TableCell>
                  <TableCell className={styles_table.tableHeadRow2} align="center">Registrado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(inscritoSelecionadoPresencas?.presencas?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell className={styles_table.textXs} align="center" colSpan={4}>
                      Nenhuma presença encontrada.
                    </TableCell>
                  </TableRow>
                )}
                {inscritoSelecionadoPresencas?.presencas?.map((presenca) => (
                  <TableRow key={presenca.id}>
                    <TableCell className={styles_table.textXs} align="center">
                      {formatarDataDbParaBr(presenca.data_presenca)}
                    </TableCell>
                    <TableCell className={styles_table.textXs} align="center">
                      <span
                        className={`${styles.resultadoBadge} ${
                          presenca.tipo === "PRESENTE"
                            ? styles.resultadoAprovado
                            : styles.resultadoReprovado
                        }`}
                      >
                        {presenca.tipo}
                      </span>
                    </TableCell>
                    <TableCell className={styles_table.textXs} align="center">
                      {presenca.origem}
                    </TableCell>
                    <TableCell className={styles_table.textXs} align="center">
                      {formatarDataHora(presenca.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className={styles.presencaModalDivider} />

          <div className={styles.presencaModalActions}>
            <Button
              className={styles.presencaModalButtonOutline}
              onClick={() => {
                setOpenVisualizarPresencas(false);
                setInscritoSelecionadoPresencas(null);
              }}
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={openConfirmAdicionarParticipante}
        loading={loadingAdicionarParticipante}
        onClose={() => {
          setOpenConfirmAdicionarParticipante(false);
          setUsuarioSelecionadoMatricula(null);
        }}
        onConfirm={handleAdicionarParticipante}
        title="Matricular participante"
        message={`Deseja matricular "${usuarioSelecionadoMatricula?.nome || "usuário"}" nesta turma?`}
        titleButtonConfirm="Matricular"
        titleLoadButton="Matriculando..."
      />

      <ConfirmDialog
        open={openConfirmProcessarResultados}
        loading={loadingProcessarResultados}
        onClose={() => setOpenConfirmProcessarResultados(false)}
        onConfirm={handleProcessarResultados}
        title="Processar resultados"
        message="Deseja processar os resultados desta turma? Durante o processamento a tela ficará bloqueada."
        titleButtonConfirm="Processar"
        titleLoadButton="Processando..."
      />

      <ConfirmDialog
        open={openConfirmMarcarTodosPresentes}
        loading={loadingMarcarTodosPresentes}
        onClose={() => setOpenConfirmMarcarTodosPresentes(false)}
        onConfirm={handleConfirmMarcarTodosPresentes}
        title="Marcar todos como presentes"
        message={`Deseja marcar todos como presentes no dia ${dataLancamentoManual ? formatarDataDbParaBr(dataLancamentoManual) : ""}?`}
        titleButtonConfirm="Confirmar"
        titleLoadButton="Processando..."
      />

      <ConfirmDialog
        open={openConfirmLimparMarcacoes}
        loading={loadingLimparMarcacoes}
        onClose={() => setOpenConfirmLimparMarcacoes(false)}
        onConfirm={handleConfirmLimparMarcacoes}
        title="Limpar marcações"
        message={`Deseja limpar as marcações do dia ${dataLancamentoManual ? formatarDataDbParaBr(dataLancamentoManual) : ""}?`}
        titleButtonConfirm="Confirmar"
        titleLoadButton="Processando..."
      />

      <ConfirmDialog
        open={openConfirmSalvarPresencas}
        loading={loadingSalvarPresencas}
        onClose={() => setOpenConfirmSalvarPresencas(false)}
        onConfirm={handleConfirmSalvarPresencas}
        title="Salvar Presenças"
        message={`Deseja salvar as Presenças do dia ${dataLancamentoManual ? formatarDataDbParaBr(dataLancamentoManual) : ""}?`}
        titleButtonConfirm="Salvar"
        titleLoadButton="Salvando..."
      />

      <ConfirmDialog
        open={openEncerrarLink}
        loading={loadingEncerrarLink}
        onClose={() => setOpenEncerrarLink(false)}
        onConfirm={handleEncerrarLinkPresenca}
        title="Encerrar QR Code ou Link"
        message="Deseja encerrar o QR Code ou link ativo desta turma?"
        titleButtonConfirm="Encerrar"
        titleLoadButton="Encerrando..."
      />

      <ConfirmDialog
        open={openConfirmCancelarInscricao}
        loading={loadingCancelarInscricao}
        onClose={() => {
          setOpenConfirmCancelarInscricao(false);
          setInscritoSelecionadoCancelamento(null);
        }}
        onConfirm={handleCancelarInscricao}
        title="Cancelar inscrição"
        message={`Deseja cancelar a inscrição de "${inscritoSelecionadoCancelamento?.nome || "participante"}" nesta turma?`}
        titleButtonConfirm="Cancelar inscrição"
        titleLoadButton="Cancelando..."
      />

      <ConfirmDialog
        open={openConfirmCancelarInscricoesMassa}
        loading={loadingCancelarInscricao}
        onClose={() => setOpenConfirmCancelarInscricoesMassa(false)}
        onConfirm={handleCancelarInscricoesMassa}
        title="Excluir usuários"
        message="Deseja realmente excluir os usuários selecionados desta turma?"
        titleButtonConfirm="Excluir"
        titleLoadButton="Excluindo..."
      />
    </Card>
  );
};

export default ControlePresenca;
